import React from 'react';
import { getDocumentStructure } from '../services/DocumentGeneratorService';
import { 
  CheckCircleIcon, 
  AlertTriangleIcon,
  CalendarIcon,
  UserIcon,
  BriefcaseIcon,
  DollarSignIcon,
  ClipboardListIcon,
  MessageSquareIcon,
  UsersIcon
} from 'lucide-react';

// Section icon mapping
const SECTION_ICONS = {
  personal_snapshot: UserIcon,
  financial_snapshot: DollarSignIcon,
  business_overview: BriefcaseIcon,
  successor_profile: UsersIcon,
  transition_timeline: CalendarIcon,
  deal_structure: DollarSignIcon,
  communication_plan: MessageSquareIcon
};

/**
 * VMI Score Bar Component
 */
const VMIScoreBar = ({ category, score, percentage }) => {
  const getStatusColor = (pct) => {
    if (pct >= 80) return 'bg-green-500';
    if (pct >= 60) return 'bg-yellow-500';
    return 'bg-red-500';
  };
  
  const getStatusText = (pct) => {
    if (pct >= 80) return 'Strong';
    if (pct >= 60) return 'Developing';
    return 'Needs Work';
  };
  
  return (
    <div className="mb-3">
      <div className="flex justify-between items-center mb-1">
        <span className="text-sm font-medium capitalize">{category}</span>
        <span className="text-sm text-gray-500">{score}/6.0 ({getStatusText(percentage)})</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div 
          className={`h-2 rounded-full ${getStatusColor(percentage)}`}
          style={{ width: `${Math.min(percentage, 100)}%` }}
        />
      </div>
    </div>
  );
};

/**
 * Gap Alert Component
 */
const GapAlert = ({ gap }) => {
  const priorityColors = {
    critical: 'bg-red-50 border-red-200 text-red-800',
    high: 'bg-orange-50 border-orange-200 text-orange-800',
    medium: 'bg-yellow-50 border-yellow-200 text-yellow-800',
    low: 'bg-blue-50 border-blue-200 text-blue-800'
  };
  
  const priorityIcons = {
    critical: '🔴',
    high: '🟠',
    medium: '🟡',
    low: '🔵'
  };
  
  return (
    <div className={`p-3 rounded-lg border ${priorityColors[gap.priority] || priorityColors.medium}`}>
      <div className="flex items-start gap-2">
        <span>{priorityIcons[gap.priority] || '⚪'}</span>
        <span className="text-sm">{gap.question}</span>
      </div>
    </div>
  );
};

/**
 * Document Section Component
 */
const DocumentSection = ({ section, index }) => {
  const Icon = SECTION_ICONS[section.key] || ClipboardListIcon;
  
  return (
    <div className="mb-8 page-break-inside-avoid">
      <div className="flex items-center gap-3 mb-4 pb-2 border-b border-gray-200">
        <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
          <Icon className="w-5 h-5 text-blue-600" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900">
            {index + 1}. {section.title}
          </h2>
          {section.description && (
            <p className="text-sm text-gray-500">{section.description}</p>
          )}
        </div>
      </div>
      
      <div className="space-y-6 pl-4">
        {section.questions.map(q => (
          <QuestionAnswer key={q.id} question={q} />
        ))}
      </div>
    </div>
  );
};

/**
 * Question Answer Component
 */
const QuestionAnswer = ({ question }) => {
  const renderAnswer = () => {
    if (!question.formattedAnswer) {
      return <span className="text-gray-400 italic">Not answered</span>;
    }
    
    switch (question.widgetType) {
      case 'textarea':
        return (
          <blockquote className="border-l-4 border-blue-200 pl-4 py-2 bg-gray-50 rounded-r-lg">
            <p className="text-gray-700 whitespace-pre-wrap">{question.formattedAnswer}</p>
          </blockquote>
        );
        
      case 'checklist':
        const items = Array.isArray(question.answer?.value) 
          ? question.answer.value 
          : [question.formattedAnswer];
        return (
          <ul className="list-disc list-inside space-y-1">
            {items.map((item, i) => (
              <li key={i} className="text-gray-700">{item}</li>
            ))}
          </ul>
        );
        
      case 'yesno':
      case 'yes-no':
        const isYes = question.answer?.value === 'yes';
        return (
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
            isYes ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
          }`}>
            {isYes && <CheckCircleIcon className="w-4 h-4 mr-1" />}
            {question.formattedAnswer}
          </span>
        );
        
      case 'currency':
        return (
          <span className="text-2xl font-bold text-gray-900">
            {question.formattedAnswer}
          </span>
        );
        
      case 'date':
        return (
          <span className="inline-flex items-center text-gray-700">
            <CalendarIcon className="w-4 h-4 mr-2 text-gray-400" />
            {question.formattedAnswer}
          </span>
        );
        
      case 'contact':
        return (
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <p className="font-medium text-gray-900 whitespace-pre-line">
              {question.formattedAnswer}
            </p>
          </div>
        );
        
      default:
        return (
          <p className="text-gray-700 font-medium">{question.formattedAnswer}</p>
        );
    }
  };
  
  return (
    <div className="page-break-inside-avoid">
      <h3 className="text-sm font-semibold text-gray-600 mb-2">
        {question.text}
      </h3>
      <div className="ml-0">
        {renderAnswer()}
      </div>
    </div>
  );
};

/**
 * Main Document Preview Component
 */
const DocumentPreview = ({ progress, userName = 'User', companyName = 'Practice', elementId = 'plan-document-preview' }) => {
  if (!progress?.planTemplate) {
    return (
      <div className="p-8 text-center text-gray-500">
        No plan data available
      </div>
    );
  }
  
  const docStructure = getDocumentStructure(progress, userName, companyName);
  
  return (
    <div id={elementId} data-plan-document-root="true" className="bg-white text-black min-h-full">
      {/* Print Styles */}
      <style>{`
        @media print {
          .page-break-inside-avoid {
            page-break-inside: avoid;
          }
          .page-break-before {
            page-break-before: always;
          }
          .no-print {
            display: none !important;
          }
        }
      `}</style>
      
      {/* Cover Page */}
      <div className="p-12 border-b-4 border-blue-600 mb-8">
        <div className="text-center">
          <h1 className="text-4xl font-serif font-bold text-gray-900 mb-2">
            Succession Plan
          </h1>
          <div className="w-24 h-1 bg-blue-600 mx-auto mb-6" />
          <h2 className="text-2xl text-gray-700 mb-1">{userName}</h2>
          <p className="text-lg text-gray-500">{companyName}</p>
        </div>
        
        <div className="mt-8 flex justify-center gap-8 text-sm text-gray-500">
          <span>Generated: {docStructure.header.generatedDate}</span>
          <span>Version: {docStructure.header.version}</span>
          <span>Status: {docStructure.header.completionPercentage}% Complete</span>
        </div>
      </div>
      
      {/* Assessment Summary */}
      {docStructure.assessment && (
        <div className="px-12 mb-8 page-break-inside-avoid">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <BriefcaseIcon className="w-6 h-6 text-blue-600" />
            Readiness Assessment
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Overall Score */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border border-blue-200">
              <p className="text-sm font-medium text-blue-600 mb-2">Overall Readiness</p>
              <p className="text-5xl font-bold text-blue-900">
                {docStructure.assessment.overallReadiness}%
              </p>
            </div>
            
            {/* VMI Scores */}
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <p className="text-sm font-medium text-gray-600 mb-3">VMI Category Scores</p>
              {Object.entries(docStructure.assessment.vmiScores).map(([category, data]) => (
                <VMIScoreBar 
                  key={category}
                  category={category}
                  score={data.score}
                  percentage={data.percentage}
                />
              ))}
            </div>
          </div>
          
          {/* Gaps */}
          {docStructure.assessment.gaps?.length > 0 && (
            <div className="mt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <AlertTriangleIcon className="w-5 h-5 text-orange-500" />
                Key Gaps Identified
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {docStructure.assessment.gaps.slice(0, 6).map((gap, i) => (
                  <GapAlert key={i} gap={gap} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Table of Contents */}
      <div className="px-12 mb-8 page-break-inside-avoid">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Table of Contents</h2>
        <ol className="list-decimal list-inside space-y-2 text-gray-700">
          {docStructure.sections.map((section, i) => (
            <li key={section.key} className="hover:text-blue-600">
              {section.title}
              <span className="text-gray-400 ml-2">({section.questions.length} items)</span>
            </li>
          ))}
        </ol>
      </div>
      
      <hr className="mx-12 border-gray-200 mb-8" />
      
      {/* Document Sections */}
      <div className="px-12 pb-12">
        {docStructure.sections.map((section, i) => (
          <DocumentSection key={section.key} section={section} index={i} />
        ))}
      </div>
      
      {/* Footer */}
      <div className="px-12 py-8 bg-gray-50 border-t border-gray-200 text-center text-sm text-gray-500">
        <p>This document is a living plan. Return to the PlanBuilder to update your answers.</p>
        <p className="mt-1">Plan ID: {progress.id} | Generated by FindBob PlanBuilder</p>
      </div>
    </div>
  );
};

export default DocumentPreview;



